import React, { useEffect, useState, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

// Three-tier visual urgency on the countdown:
//   normal   — neutral grey background, body text colour
//   warn     — orange tint when ≤60s remain (one-minute warning)
//   critical — red tint when ≤10s remain (final stretch)
const LEVEL_STYLES = {
  normal:   { bg: 'rgba(0, 0, 0, 0.04)',  fg: null },
  warn:     { bg: 'rgba(234, 88, 12, 0.10)', fg: '#c2410c' },
  critical: { bg: 'rgba(220, 38, 38, 0.12)', fg: '#dc2626' },
};

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'level',
})(({ theme, level }) => {
  const style = LEVEL_STYLES[level] || LEVEL_STYLES.normal;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 96,
    padding: '6px 12px',
    borderRadius: 8,
    fontWeight: 600,
    background: style.bg,
    color: style.fg || theme?.palette?.text?.primary || '#222',
    fontVariantNumeric: 'tabular-nums',
    boxSizing: 'border-box',
    transition: 'background-color 200ms ease, color 200ms ease',
  };
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme?.palette?.grey?.[600] || '#666',
  marginRight: 8,
}));

const Time = styled(Typography)(() => ({
  fontSize: 18,
  fontWeight: 700,
}));

function formatMMSS(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Countdown for the Wonderlic phase. Receives the server-stamped phase start
 * time and the time limit, computes a clock-skew offset against the server's
 * `now` reading, and ticks every 250ms. Calls `onTimeout` exactly once when
 * the deadline passes.
 *
 * Refresh-safe: derives remaining time from `phaseStartedAt` rather than from
 * a local start moment, so reloads keep the same countdown.
 *
 * Props:
 *   phaseStartedAt: ISO string from server
 *   timeLimitSeconds: number (required — comes from `/assessment/wonderlic/start`)
 *   serverNow: ISO string of server clock at the time we received the start
 *   onTimeout: () => void, fired once when the deadline passes
 */
export default function WonderlicTimer({
  phaseStartedAt,
  timeLimitSeconds,
  serverNow,
  onTimeout,
}) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(timeLimitSeconds);
  const firedRef = useRef(false);
  const offsetRef = useRef(0);

  // Compute the offset between client clock and server clock once we know
  // both. Positive offset = client is ahead of server.
  useEffect(() => {
    if (serverNow) {
      const serverMs = new Date(serverNow).getTime();
      offsetRef.current = Date.now() - serverMs;
    }
  }, [serverNow]);

  useEffect(() => {
    if (!phaseStartedAt) return undefined;
    const startMs = new Date(phaseStartedAt).getTime();
    if (Number.isNaN(startMs)) return undefined;

    const tick = () => {
      // Treat client `Date.now() - offset` as the server's view of "now".
      const serverNowMs = Date.now() - offsetRef.current;
      const elapsed = (serverNowMs - startMs) / 1000;
      const left = Math.max(0, timeLimitSeconds - elapsed);
      setRemaining(left);
      if (left <= 0 && !firedRef.current) {
        firedRef.current = true;
        if (typeof onTimeout === 'function') onTimeout();
      }
    };

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [phaseStartedAt, timeLimitSeconds, onTimeout]);

  const level = remaining <= 10 ? 'critical' : remaining <= 60 ? 'warn' : 'normal';

  return (
    <Box>
      <Container level={level}>
        <Label>{t('Assessment.timerLabel', { defaultValue: 'Час' })}</Label>
        <Time>{formatMMSS(remaining)}</Time>
      </Container>
    </Box>
  );
}
